import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Compass, Target, Clock, Sparkle, ArrowClockwise, Robot } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Diagnostic() {
  const { user } = useAuth();
  const [plan, setPlan] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [form, setForm] = useState({ weekly_hours: 6, weak_subjects: [], goal: "" });
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    api.get("/diagnostic").then((r) => { setPlan(r.data.study_plan); setShowForm(!r.data.study_plan); });
    if (user?.exam_id) api.get(`/exams/${user.exam_id}`).then((r) => setSubjects(r.data.subjects.map((s) => s.name)));
  }, [user]);

  const toggleSubject = (s) => setForm((f) => ({
    ...f, weak_subjects: f.weak_subjects.includes(s) ? f.weak_subjects.filter((x) => x !== s) : [...f.weak_subjects, s],
  }));

  const submit = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/diagnostic", { ...form, goal: form.goal || "Réussir mon examen" });
      setPlan(data.study_plan);
      setShowForm(false);
      toast.success("Votre plan de réussite est prêt ! (+20 XP)");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Génération impossible (Prof IA indisponible).");
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-8">
      <div className="animate-fade-up">
        <span className="text-xs font-semibold uppercase tracking-[0.25em] text-terracotta">Diagnostic & personnalisation</span>
        <h1 className="mt-2 flex items-center gap-2 font-display text-3xl font-extrabold sm:text-4xl"><Compass weight="fill" className="text-terracotta" /> Mon plan de réussite</h1>
        <p className="mt-1 text-ink/65">Le Professeur IA analyse votre profil et crée un planning de révision sur-mesure.</p>
      </div>

      {showForm ? (
        <div className="space-y-6 rounded-2xl border border-border bg-white p-6">
          <div>
            <label className="flex items-center gap-2 text-sm font-bold text-forest"><Clock size={18} /> Temps de révision disponible par semaine</label>
            <div className="mt-2 flex items-center gap-3">
              <input data-testid="diag-hours" type="range" min="2" max="30" value={form.weekly_hours}
                onChange={(e) => setForm({ ...form, weekly_hours: Number(e.target.value) })} className="flex-1 accent-terracotta" />
              <span className="w-16 rounded-lg bg-sand px-3 py-1 text-center text-sm font-bold">{form.weekly_hours}h</span>
            </div>
          </div>
          <div>
            <label className="flex items-center gap-2 text-sm font-bold text-forest"><Target size={18} /> Matières où vous êtes le plus faible</label>
            <div className="mt-2 flex flex-wrap gap-2">
              {subjects.map((s) => (
                <button key={s} data-testid={`diag-subject-${s}`} onClick={() => toggleSubject(s)}
                  className={`rounded-full border-2 px-3 py-1.5 text-sm font-medium transition ${form.weak_subjects.includes(s) ? "border-terracotta bg-terracotta text-white" : "border-border hover:bg-sand"}`}>
                  {s}
                </button>
              ))}
              {!subjects.length && <span className="text-sm text-muted-foreground">Choisissez d'abord un examen dans votre profil.</span>}
            </div>
          </div>
          <div>
            <label className="text-sm font-bold text-forest">Votre objectif</label>
            <input data-testid="diag-goal" value={form.goal} onChange={(e) => setForm({ ...form, goal: e.target.value })}
              placeholder="Ex : obtenir une mention au Baccalauréat" className="mt-2 w-full rounded-xl border border-border px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-terracotta" />
          </div>
          <Button data-testid="diag-submit" onClick={submit} disabled={loading} className="w-full rounded-full bg-terracotta py-6 text-white hover:bg-[#E03E00]">
            {loading ? "Le Professeur IA élabore votre plan…" : <><Sparkle weight="fill" className="mr-2" /> Générer mon plan de réussite</>}
          </Button>
        </div>
      ) : plan ? (
        <>
          <div className="prose-yaya rounded-2xl border border-border bg-white p-6 text-sm">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{plan.content}</ReactMarkdown>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button data-testid="diag-regenerate" onClick={() => setShowForm(true)} variant="outline" className="rounded-full"><ArrowClockwise className="mr-1.5" /> Refaire le diagnostic</Button>
            <Link to="/app/tuteur" className="inline-flex items-center gap-2 rounded-full bg-forest px-5 py-2.5 text-sm font-semibold text-white hover:bg-forest/90"><Robot weight="fill" /> Ajuster avec le Prof IA</Link>
          </div>
        </>
      ) : (
        <div className="h-40 animate-pulse rounded-2xl bg-sand" />
      )}
    </div>
  );
}
